import { JSX } from "react";
import { I18nextProvider } from "react-i18next";
import {
	createBrowserRouter,
	createRoutesFromElements,
	Navigate,
	Route,
	RouterProvider
} from "react-router-dom";

import i18n from "i18next";

import "./App.css";
import theme from "./Theme";

import { StyledEngineProvider, ThemeProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";

import ActivationPage from "authentication/ActivationPage";
import { AuthProvider } from "authentication/AuthProvider";
import LoginPage from "authentication/LoginPage";
import OAuth2LoginRedirect from "authentication/OAuth2LoginRedirect";
import RegisterPage from "authentication/RegisterPage";
import RequestPasswordResetPage from "authentication/RequestPasswordResetPage";
import ResetPasswordPage from "authentication/ResetPasswordPage";

import TrajectoryOverview from "trajectory/TrajectoryOverview";
import TrajectoryView from "trajectory/TrajectoryView";

import Profile from "user/Profile";

import ErrorPage from "ErrorPage";

import BlueprintOverview from "core/blueprint/base/BlueprintOverview";
import CompetenceListView from "core/blueprint/competencelist/CompetenceListView";
import DashboardBlueprintPage from "core/blueprint/dashboard/DashboardBlueprintPage";
import InformationBlueprintPage from "core/blueprint/information/InformationBlueprintPage";
import ReportBlueprintView from "core/blueprint/report/ReportBlueprintView";
import TrajectoryBlueprintView from "core/blueprint/trajectory/TrajectoryBlueprintView";
import TrashPage from "core/trash/TrashPage";

import ExtractOverview from "extract/ExtractOverview";
import ExtractBlueprintView from "extractblueprint/ExtractBlueprintView";

import CreateOrganisationPage from "organisation/CreateOrganisationPage";
import EditOrganisationPage from "organisation/EditOrganisationPage";

import Authenticated from "Authenticated";
import ErrorBoundary from "ErrorBoundary";
import Page from "utils/Page";

import { StatusProvider } from "status/StatusProvider";
import ResourcePage from "utils/ResourcePage";

// TODO: fix paths: singular vs plural.
const router = createBrowserRouter(
	createRoutesFromElements(
		<Route errorElement={<ErrorBoundary />}>
			<Route path="/login" element={<LoginPage />} />

			<Route element={<Page />}>
				<Route index element={<Navigate to="trajectories" replace />} />

				<Route path="/register" element={<RegisterPage />} />
				<Route path="/activate" element={<ActivationPage />} />
				<Route
					path="/create-organisation"
					element={<CreateOrganisationPage />}
				/>
				<Route
					path="/request-password-reset"
					element={<RequestPasswordResetPage />}
				/>
				<Route path="/reset-password" element={<ResetPasswordPage />} />
				<Route
					path="/oauth2/code/:provider"
					element={<OAuth2LoginRedirect />}
				/>

				{/* TODO: distinguish pages that are allowed without organisation. */}
				<Route element={<Authenticated />}>
					<Route path="/profile" element={<Profile />} />
					<Route
						path="/trajectories"
						element={<TrajectoryOverview />}
					/>
					<Route
						path="/trajectory/:id/*"
						element={<TrajectoryView />}
					/>
					<Route path="blueprints">
						<Route index element={<BlueprintOverview />} />
						<Route
							path="competencelists/:id"
							element={<CompetenceListView />}
						/>
						<Route
							path="reports/:id"
							element={<ReportBlueprintView />}
						/>
						<Route
							path="extracts/:id"
							element={<ExtractBlueprintView />}
						/>
						<Route
							path="trajectories/:id"
							element={<TrajectoryBlueprintView />}
						/>
						<Route
							path="dashboards/:id"
							element={<DashboardBlueprintPage />}
						/>
						<Route
							path="information/:id"
							element={
								<ResourcePage
									render={InformationBlueprintPage}
								/>
							}
						/>
					</Route>
					<Route path="/extracts" element={<ExtractOverview />} />
					<Route path="/trash" element={<TrashPage />} />

					<Route path="organisations">
						<Route path=":id" element={<EditOrganisationPage />} />
					</Route>
				</Route>
				<Route path="*" element={<ErrorPage />} />
			</Route>
		</Route>
	)
);

function App(): JSX.Element {
	Error.stackTraceLimit = 50;

	return (
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<StatusProvider>
					<AuthProvider>
						<I18nextProvider i18n={i18n}>
							<RouterProvider router={router} />
						</I18nextProvider>
					</AuthProvider>
				</StatusProvider>
			</ThemeProvider>
		</StyledEngineProvider>
	);
}

export default App;
